"use client";
/**
 * 生产可见 Error Boundary。
 *
 * Next.js 默认的 production error 兜底页只显示 "Application error: a client-side
 * exception has occurred (see the browser console for more information)"，
 * 用户不会自己开 console，运维定位也很痛。这个 boundary 把真实的 error.message
 * + stack 直接渲染到页面（带"复制错误"按钮），让用户能直接截图给我们。
 *
 * 不动 next.config.mjs 的 productionBrowserSourceMaps —— 浏览器 Sources 面板
 * 仍可定位到原源码，但页面文案先让运维 / 用户能复述。
 */
import * as React from "react";

const CLIENT_RECOVERY_RELOAD_KEY = "xyt_client_recovery_reload_at";
const CLIENT_RECOVERY_RELOAD_COOLDOWN_MS = 5 * 60 * 1000;

function isReloadRecoverableClientError(error: Error): boolean {
  const msg = `${error.message || ""}\n${error.stack || ""}`;
  return /Minified React error #(310|418|421|422|423|425)|ChunkLoadError|Loading chunk \S+ failed|Loading CSS chunk|Failed to fetch dynamically imported module/i.test(msg);
}

function tryAutoReloadOnce(error: Error) {
  if (typeof window === "undefined" || !isReloadRecoverableClientError(error)) return;
  let last = 0;
  try {
    last = Number(window.sessionStorage.getItem(CLIENT_RECOVERY_RELOAD_KEY) || 0);
  } catch {
    last = 0;
  }
  if (Date.now() - last < CLIENT_RECOVERY_RELOAD_COOLDOWN_MS) return;
  try {
    window.sessionStorage.setItem(CLIENT_RECOVERY_RELOAD_KEY, String(Date.now()));
  } catch {
    // private mode / disabled storage: still try one reload for this in-memory page.
  }
  window.setTimeout(() => window.location.reload(), 80);
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

export class VisibleErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, componentStack: null };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ componentStack: info.componentStack || null });
    tryAutoReloadOnce(error);
    if (typeof console !== "undefined") {
      // eslint-disable-next-line no-console
      console.error("[VisibleErrorBoundary]", error, info);
    }
  }

  reset = () => this.setState({ error: null, componentStack: null });

  copy = async () => {
    const { error, componentStack } = this.state;
    const text = [
      `Error: ${error?.message || "unknown"}`,
      "",
      "Stack:",
      error?.stack || "(no stack)",
      "",
      "Component stack:",
      componentStack || "(no component stack)",
      "",
      `URL: ${typeof location !== "undefined" ? location.href : "?"}`,
      `UA: ${typeof navigator !== "undefined" ? navigator.userAgent : "?"}`,
      `Time: ${new Date().toISOString()}`,
    ].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      alert("错误信息已复制到剪贴板，请粘贴给运维 / 开发同学");
    } catch {
      // 降级：选中 textarea 让用户手动复制
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* ignore */ }
      ta.remove();
      alert("已尝试复制（旧浏览器），请检查剪贴板");
    }
  };

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;
    return (
      <div style={{ padding: 24, maxWidth: 960, margin: "40px auto", fontFamily: "system-ui, sans-serif" }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, color: "#dc2626" }}>
          ⚠️ 页面出错了
        </h1>
        <p style={{ color: "#6b7280", marginTop: 4 }}>
          请把下面的错误信息复制给运维 / 开发，我们能直接定位到原因。
        </p>
        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <button
            onClick={this.copy}
            style={{ padding: "6px 12px", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", background: "#f9fafb" }}
          >
            📋 复制错误信息
          </button>
          <button
            onClick={this.reset}
            style={{ padding: "6px 12px", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", background: "#f9fafb" }}
          >
            🔄 重试渲染
          </button>
          <button
            onClick={() => location.reload()}
            style={{ padding: "6px 12px", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", background: "#f9fafb" }}
          >
            ⟳ 刷新页面
          </button>
        </div>
        <pre
          style={{
            marginTop: 16,
            padding: 12,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 6,
            color: "#991b1b",
            fontSize: 13,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            maxHeight: 320,
            overflow: "auto",
          }}
        >
{`Error: ${error.message}

Stack:
${error.stack || "(no stack)"}

Component stack:
${componentStack || "(no component stack)"}

URL: ${typeof location !== "undefined" ? location.href : "?"}
UA: ${typeof navigator !== "undefined" ? navigator.userAgent : "?"}`}
        </pre>
      </div>
    );
  }
}
